import React, { Component } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import { apiErrorMessage } from "Helpers/ContentValidation";

import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Chip, Grid, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

/**
 * Leadership profiles shown in the "Leadership Profile" section of the
 * website's About page. No photos — the company does not show team pictures.
 */
export default class Leaders extends Component {
  state = {
    leaders: [],
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchLeaders();
  }

  /* =============================
     FETCH
  ============================= */
  fetchLeaders = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true });

    try {
      // The list is short and the table paginates on the client.
      const response = await axios.get(`${Constants.getUrls.getAllLeaders}?limit=200`);
      this.setState({ leaders: response.data?.data?.docs || [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not load leaders"));
    }
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /* =============================
     STATUS
  ============================= */
  handleToggleStatus = async (leader) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.patch(Constants.patchUrls.toggleLeaderStatus + leader._id);
      this.notify("success", response.data?.message || "Status updated");
      this.fetchLeaders();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not change the status"));
    }
  };

  /* =============================
     DELETE
  ============================= */
  handleDelete = async (leader) => {
    if (init() !== "success") return this.setState({ redirect: true });
    if (!window.confirm(`Delete the profile of "${leader.name}"?`)) return;

    try {
      const response = await axios.delete(Constants.deleteUrls.deleteLeader + leader._id);
      this.notify("success", response.data?.message || "Leader deleted");
      this.fetchLeaders();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not delete the leader"));
    }
  };

  /* =============================
     RENDER
  ============================= */
  render() {
    const { leaders, loading, redirect } = this.state;

    if (redirect) return <Navigate to="/signin" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Leadership
              </MDTypography>
              <MDTypography variant="caption" color="white">
                Profiles in the Leadership Profile section of the About page
              </MDTypography>
            </MDBox>

            <Grid item sm={4} px={2} pt={2} align="right">
              <Link to="/addleader">
                <MDButton variant="contained" size="small" color="primary">
                  Add Leader
                </MDButton>
              </Link>
            </Grid>

            <MDBox py={3}>
              {loading ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : leaders.length > 0 ? (
                <DataTable
                  canSearch
                  entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50, 100] }}
                  table={{
                    columns: [
                      { Header: "Order", accessor: "order", width: "6%" },
                      { Header: "Name", accessor: "name" },
                      { Header: "Designation", accessor: "designation" },
                      { Header: "Paragraphs", accessor: "paragraphs" },
                      { Header: "Status", accessor: "status" },
                      { Header: "Actions", accessor: "action" },
                    ],
                    rows: leaders.map((leader) => ({
                      order: leader.sort_order ?? 0,
                      name: leader.name,
                      designation: leader.designation || "—",
                      paragraphs: leader.bio?.length || 0,

                      status: (
                        <Chip
                          size="small"
                          label={leader.status === "active" ? "Active" : "Inactive"}
                          color={leader.status === "active" ? "success" : "default"}
                        />
                      ),

                      action: (
                        <div style={{ display: "flex", gap: 6 }}>
                          <Tooltip title="Edit leader">
                            <Link to={`/updateleader/${leader._id}`}>
                              <IconButton size="small" color="info">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip
                            title={
                              leader.status === "active"
                                ? "Hide from the website"
                                : "Show on the website"
                            }
                          >
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => this.handleToggleStatus(leader)}
                            >
                              {leader.status === "active" ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete leader">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => this.handleDelete(leader)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ),
                    })),
                  }}
                />
              ) : (
                <div style={{ padding: 20 }}>No Leaders Found!</div>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Leadership"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
